/**
 * Live Stream Chat unread count hook.
 *
 * Subscribes to `notification.mark_read`, `notification.message_new`, and
 * `message.new` events and exposes the current `total_unread_count` without
 * polling. Safe to call before the client is connected; the hook returns 0
 * until the connection is established.
 */
import { useEffect, useState } from 'react';
import type { Event } from 'stream-chat';
import { streamClient } from './streamClient';

export interface StreamUnreadState {
  total: number;
  hasUnread: boolean;
}

export function useStreamUnreadCount(): StreamUnreadState {
  const [total, setTotal] = useState<number>(() => {
    const u = (streamClient as unknown as { user?: { total_unread_count?: number } }).user;
    return typeof u?.total_unread_count === 'number' ? u.total_unread_count : 0;
  });

  useEffect(() => {
    const recompute = (e?: Event) => {
      const fromEvent =
        typeof e?.total_unread_count === 'number' ? e.total_unread_count : null;
      if (fromEvent !== null) {
        setTotal(fromEvent);
        return;
      }
      const u = (streamClient as unknown as { user?: { total_unread_count?: number } }).user;
      if (typeof u?.total_unread_count === 'number') setTotal(u.total_unread_count);
    };

    recompute();
    const subs = [
      streamClient.on('notification.message_new', recompute),
      streamClient.on('notification.mark_read', recompute),
      streamClient.on('message.new', recompute),
      streamClient.on('connection.changed', recompute),
    ];
    return () => {
      for (const s of subs) s.unsubscribe();
    };
  }, []);

  return { total, hasUnread: total > 0 };
}
